import { apiRequest, queryClient } from '@/lib/queryClient';
import type { CapacityResult } from '@shared/scheduling/capacity';
import type { ScheduleBuildModeValue } from '@shared/validation/seatFirst';
import type { TravelTier } from '@shared/geo/travelTier';

export interface DivisionScheduleSettings {
  divisionId: string;
  name: string;
  scheduleBuildMode: ScheduleBuildModeValue | null;
  effectiveBuildMode: ScheduleBuildModeValue;
  seatBuildStatus: 'NONE' | 'BUILDING' | 'BUILT' | 'FINAL';
  playoffSeatStatus: 'NONE' | 'BUILT' | 'STALE';
  schedulePreferences: unknown;
  playoffScheduleConfig: unknown;
}

export interface ScheduleSettingsResponse {
  defaultScheduleBuildMode: ScheduleBuildModeValue;
  scheduleVersion: number;
  divisions: DivisionScheduleSettings[];
}

export interface DivisionCapacityResponse extends CapacityResult {
  teamCount: number;
  poolCount: number;
  poolSizes: number[];
  gameSlotMinutes: number;
  missingAllocationDates?: string[];
}

export interface TeamTravelProfile {
  teamId: string;
  name: string;
  divisionId: string | null;
  tier: TravelTier;
  rank: number;
  distanceKm: number | null;
  isTravelling: boolean;
  isProtected: boolean;
  unknownDistance: boolean;
  pinned: boolean;
  postalCode: string | null;
  travelOverride: 'protected' | 'not_protected' | null;
  allowExtraFridayGame: boolean;
}

export interface TravelProfilesResponse {
  localRadiusKm: number;
  profiles: TeamTravelProfile[];
  unknownCount: number;
}

export type ScheduleJobStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';

export interface SeatBuildStartResponse {
  jobId: string;
  deduped: boolean;
  scheduleVersion: number | null;
  seatCount: number;
  matchupCount: number;
  divisionIds?: string[];
  allocationsFilled?: { created: number; datesFilled: string[] };
}

export interface SeatRuleSummary {
  rule: 'R1' | 'R2' | 'R3' | 'R4' | 'REST';
  status: 'pass' | 'bent' | 'warning';
  count: number;
  seatIds: string[];
}

export interface SeatBuildReport {
  engine: 'cp_sat' | 'greedy_fallback' | null;
  fallbackReason: string | null;
  usedSlack: boolean;
  softScore: { total: number; breakdown: Record<string, number> };
  unplacedMatchupIds: string[];
  playoffNote?: string | null;
  seed: number;
  rules: SeatRuleSummary[];
  seats: Array<{
    id: string;
    name: string;
    poolName: string;
    travelFriendly: boolean;
    games: Array<{
      gameId: string;
      date: string | null;
      time: string | null;
      diamondName: string | null;
      opponentSeatId: string;
    }>;
  }>;
}

export interface SeatJobResponse {
  job: {
    id: string;
    kind: string;
    status: ScheduleJobStatus;
    errorCode: string | null;
    createdAt: string;
    finishedAt: string | null;
  };
  report: SeatBuildReport | null;
}

export const seatKeys = {
  settings: (tournamentId: string) =>
    ['/api/tournaments', tournamentId, 'schedule-settings'] as const,
  board: (tournamentId: string, divisionId: string) =>
    ['/api/tournaments', tournamentId, 'divisions', divisionId, 'seats'] as const,
  job: (tournamentId: string, divisionId: string, jobId: string) =>
    ['/api/tournaments', tournamentId, 'divisions', divisionId, 'seats', 'jobs', jobId] as const,
  travel: (tournamentId: string) => ['/api/tournaments', tournamentId, 'travel-profiles'] as const,
  capacityRoot: (tournamentId: string, divisionId: string) =>
    ['/api/tournaments', tournamentId, 'divisions', divisionId, 'seats', 'capacity'] as const,
  capacity: (tournamentId: string, divisionId: string, teamCount?: number, poolCount?: number) =>
    [...seatKeys.capacityRoot(tournamentId, divisionId), { teamCount, poolCount }] as const,
};

export async function fetchDivisionCapacity(
  tournamentId: string,
  divisionId: string,
  teamCount?: number,
  poolCount?: number
): Promise<DivisionCapacityResponse> {
  const params = new URLSearchParams();
  if (teamCount) params.set('teamCount', String(teamCount));
  if (poolCount) params.set('poolCount', String(poolCount));
  const qs = params.toString();
  const res = await apiRequest(
    'GET',
    `/api/tournaments/${tournamentId}/divisions/${divisionId}/seats/capacity${qs ? `?${qs}` : ''}`
  );
  return res.json();
}

export function invalidateSeatSettings(tournamentId: string, divisionId?: string): void {
  void queryClient.invalidateQueries({ queryKey: seatKeys.settings(tournamentId) });
  if (divisionId) {
    void queryClient.invalidateQueries({
      queryKey: seatKeys.capacityRoot(tournamentId, divisionId),
    });
  }
}
